import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ChevronDown } from 'lucide-react';

export function MultiSelectFilter({ label, options, selected, onSelectionChange, testId }) {
  const [open, setOpen] = useState(false);

  const allSelected = selected.length === 0;

  const toggle = (value) => {
    if (selected.includes(value)) {
      onSelectionChange(selected.filter(v => v !== value));
    } else {
      onSelectionChange([...selected, value]);
    }
  };

  const clearAll = () => onSelectionChange([]);

  const displayText = allSelected
    ? `All ${label}`
    : selected.length === 1
      ? options.find(o => o.value === selected[0])?.label || selected[0]
      : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid={testId}
          className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700 text-xs sm:text-sm h-8 sm:h-9 min-w-fit sm:min-w-[140px] px-2 sm:px-3 justify-between font-manrope whitespace-nowrap"
        >
          <span className={`truncate text-xs sm:text-sm ${allSelected ? 'text-zinc-400' : 'text-white'}`}>{displayText}</span>
          <ChevronDown className="w-3.5 h-3.5 ml-1 sm:ml-2 text-zinc-500 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="bg-zinc-950 border-zinc-800 p-2 w-48 sm:w-52" align="start">
        <button
          onClick={clearAll}
          data-testid={`${testId}-all`}
          className={`w-full text-left px-2 py-1.5 rounded text-xs font-manrope transition-colors ${
            allSelected ? 'text-brand-400 bg-brand-500/10' : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
          }`}
        >
          All {label}
        </button>
        <div className="border-t border-zinc-800 my-1" />
        {options.map(opt => (
          <label
            key={opt.value}
            data-testid={`${testId}-${opt.value}`}
            className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-zinc-900 transition-colors"
          >
            <Checkbox
              checked={selected.includes(opt.value)}
              onCheckedChange={() => toggle(opt.value)}
              className="h-3.5 w-3.5 border-zinc-600 data-[state=checked]:bg-brand-500 data-[state=checked]:border-brand-500"
            />
            <span className="text-xs font-manrope text-zinc-300">{opt.label}</span>
            {opt.badge && (
              <Badge className={`${opt.badge} border text-[10px] rounded-full ml-auto px-1.5 py-0`}>
                {opt.badgeText}
              </Badge>
            )}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}
